import DashboardLayout from "lib/components/layouts/dashboard";
import PractitionerProfileCard from "lib/components/profile/practitioner-profile-card";
import usePractitioner from "lib/hooks/usePractitioner";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "lib/contexts/app-context";
import ErrorBox from "lib/components/common/error-box";
import useTherapies from "lib/hooks/useTherapies";
import { toast } from "sonner";

const ProfilePage = () => {
  const { appState } = useContext(AppContext);
  const {
    addPractitionerTherapies,
    getPractitionerTherapies,
    deletePractitionerTherapy,
    updatePractitionerProfilePic,
    updatePractitionerDetails,
    getPractitionerDetails,
    getPractitionerProfilePic,
    getPractitionerDocuments,
    updatePractitionerDocuments,
    getPractitionerSubscriptions,
    loading: practitionerLoading,
    error: practitionerError,
  } = usePractitioner();
  const {
    therapies,
    getTherapies,
    error: therapiesError,
    loading: therapiesLoading,
  } = useTherapies();

  const [practitionerDetails, setPractitionerDetails] =
    useState<PractitionerDetails | null>(null);
  const [practitionerTherapies, setPractitionerTherapies] = useState<
    PractitionerTherapy[]
  >([]);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [practitionerDocuments, setPractitionerDocuments] =
    useState<PractitionerDocuments>(null);
  const [practitionerSubscriptions, setPractitionerSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);

  const refreshDocuments = (
    subscriptions?: PractitionerSubscriptionDetails[]
  ) => {
    if (practitionerDetails && (practitionerSubscriptions || subscriptions)) {
      getPractitionerDocuments(
        practitionerDetails,
        subscriptions ?? practitionerSubscriptions,
        (documents) => {
          setPractitionerDocuments(documents);
          if (documents?.profileImageUrl) {
            setProfilePicUrl(documents?.profileImageUrl);
          }
          if (documents?.educationalCertificateUrl) {
            setPractitionerDocuments({
              ...documents,
              educationalCertificateUrl: documents?.educationalCertificateUrl,
            });
          }
        }
      );
    }
  };

  const refreshData = async () => {
    getPractitionerDetails(appState.user_id, (value) => {
      setPractitionerDetails(value);
      getPractitionerTherapies(value?.id, setPractitionerTherapies);
      getPractitionerProfilePic(value?.id, setProfilePicUrl);
      getPractitionerSubscriptions(value?.id, (subscriptions) => {
        setPractitionerSubscriptions(subscriptions);
        refreshDocuments(subscriptions);
      });
    });
  };

  useEffect(() => {
    refreshData();
    getTherapies();
  }, []);

  useEffect(
    () => refreshDocuments(),
    [practitionerDetails, practitionerSubscriptions]
  );

  const handleProfileUpdates = (values: PractitionerDetails) => {
    if (!practitionerDetails?.id) {
      toast.error(
        "Unable to update profile. Please refresh the page and try again."
      );
      return;
    }
    updatePractitionerDetails(
      practitionerDetails?.id,
      values,
      setPractitionerDetails
    );
  };

  const handleTherapiesUpdate = (values: PractitionerTherapy[]) => {
    if (!practitionerDetails?.id) {
      toast.error(
        "Unable to update profile. Please refresh the page and try again."
      );
      return;
    }
    addPractitionerTherapies(
      practitionerDetails?.id,
      values,
      setPractitionerTherapies
    );
  };

  const handleTherapyDelete = (values: PractitionerTherapy) => {
    if (!values?.practitionerId || !values?.therapyId) {
      toast.error(
        "Unable to delete therapy. Please refresh the page and try again."
      );
      return;
    }

    deletePractitionerTherapy(values?.practitionerId, values?.therapyId, () => {
      getPractitionerTherapies(
        practitionerDetails?.id,
        setPractitionerTherapies
      );
    });
  };

  const handleProfilePicUpdate = (values: File) => {
    if (!practitionerDetails?.id) {
      toast.error(
        "Unable to update profile. Please refresh the page and try again."
      );
      return;
    }
    setProfilePicUrl(null);
    updatePractitionerProfilePic(practitionerDetails?.id, values, () => {
      refreshDocuments();
    });
  };

  const handlePractitionerDocumentsUpdate = (certificate: File) => {
    if (!practitionerDetails?.id) {
      toast.error(
        "Unable to update profile. Please refresh the page and try again."
      );
      return;
    }

    setPractitionerDocuments(null);
    updatePractitionerDocuments(practitionerDetails?.id, certificate, () => {
      refreshDocuments();
    });
  };

  return (
    <DashboardLayout>
      {therapiesError || practitionerError ? (
        <ErrorBox message={therapiesError ?? practitionerError} />
      ) : (
        <PractitionerProfileCard
          isLoading={therapiesLoading || practitionerLoading}
          profilePicUrl={profilePicUrl}
          isEditable={true}
          practitionerDetails={practitionerDetails}
          onProfileUpdate={handleProfileUpdates}
          therapies={therapies}
          practitionerTherapies={practitionerTherapies}
          onTherapiesUpdate={handleTherapiesUpdate}
          onTherapyDelete={handleTherapyDelete}
          onProfilePicUpdate={handleProfilePicUpdate}
          practitionerDocuments={practitionerDocuments}
          onCertificateUpdate={handlePractitionerDocumentsUpdate}
          practitionerSubscriptions={practitionerSubscriptions}
        />
      )}
    </DashboardLayout>
  );
};

export default ProfilePage;
